import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BaseServices } from '../../kuba.services';
import { FlexiCustomerRequestService } from './flexi-customer-request.service';
import {
  ADDITIONAL_MODULE_OPTIONS,
  ADDITIONAL_SERVICE_OPTIONS,
  FlexiRequestOption,
  FlexiRequestType
} from './flexi-customer-request.config';

/**
 * Shared "request a quote" form for both Flexi customer-request tabs.
 * The route supplies `data.requestType` (0 = services, 1 = modules); the
 * component picks the matching option catalog and i18n title keys. One
 * component, two routes — no duplicate markup.
 *
 * Visible to every business user. Admins additionally see a
 * recipient-override input for testing — the backend ignores it for
 * everyone else. Portal users in the Remote dashboard window see the form
 * read-only.
 */
@Component({
  selector: 'flexi-customer-request-form',
  templateUrl: './customer-request-form.component.html',
  styleUrls: ['./customer-request.scss'],
  standalone: false
})
export class CustomerRequestFormComponent implements OnInit {
  requestType: FlexiRequestType = 0;
  options: FlexiRequestOption[] = [];

  /** i18n key roots resolved from requestType. */
  titleKey = '';
  introKey = '';

  selected = new Set<string>();
  comments = '';

  /** Testing aid — only privileged users see + can use this. */
  canOverrideRecipient = false;
  overrideRecipientEmail = '';

  isPortalRemoteReadOnly = false;

  submitting = false;
  successMessage: string | null = null;
  errorMessage: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private service: FlexiCustomerRequestService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.isPortalRemoteReadOnly =
      BaseServices.IsRmode && String(BaseServices.AbsoluteroleId) === '2';

    // Route data drives which form this is.
    const rt = Number(this.route.snapshot.data['requestType']);
    this.requestType = (rt === 1 ? 1 : 0) as FlexiRequestType;

    if (this.requestType === 1) {
      this.options = ADDITIONAL_MODULE_OPTIONS;
      this.titleKey = 'FLEXI_CUSTOMER_REQUEST.MODULES.TITLE';
      this.introKey = 'FLEXI_CUSTOMER_REQUEST.MODULES.INTRO';
    } else {
      this.options = ADDITIONAL_SERVICE_OPTIONS;
      this.titleKey = 'FLEXI_CUSTOMER_REQUEST.SERVICES.TITLE';
      this.introKey = 'FLEXI_CUSTOMER_REQUEST.SERVICES.INTRO';
    }

    // Admin only may redirect the quote email while testing. AbsoluteroleId
    // stays '1' even in R-mode, so this covers admin-direct + admin-in-R-mode
    // and excludes Portal (editor-equivalent in Flexi). The backend enforces
    // the same — a non-admin override is ignored server-side regardless.
    this.canOverrideRecipient = String(BaseServices.AbsoluteroleId) === '1';
  }

  toggle(key: string): void {
    if (this.isPortalRemoteReadOnly) return;
    if (this.selected.has(key)) this.selected.delete(key);
    else this.selected.add(key);
    // Editing after a result clears the banner so it doesn't linger.
    this.successMessage = null;
    this.errorMessage = null;
  }

  get canSend(): boolean {
    return ( !this.isPortalRemoteReadOnly && this.selected.size > 0 && !this.submitting
    );
  }

  send(): void {
    if (this.isPortalRemoteReadOnly || !this.canSend) return;
    this.submitting = true;
    this.successMessage = null;
    this.errorMessage = null;

    this.service.submit(BaseServices.BusinessId, {
      requestType: this.requestType,
      selectedKeys: Array.from(this.selected),
      comments: this.comments.trim() || null,
      overrideRecipientEmail:
        this.canOverrideRecipient && this.overrideRecipientEmail.trim()
          ? this.overrideRecipientEmail.trim()
          : null
    }).subscribe({
      next: res => {
        this.submitting = false;
        this.successMessage = this.translate.instant('FLEXI_CUSTOMER_REQUEST.SENT', { email: res.sentTo });
        // Reset the form for a clean slate after a successful send.
        this.selected.clear();
        this.comments = '';
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        this.errorMessage = err.error?.detail
          ?? this.translate.instant('FLEXI_CUSTOMER_REQUEST.SEND_FAILED');
      }
    });
  }
}
